import React, { useState, useEffect } from "react";
import { ArrowRight, Clock, CheckCircle } from "lucide-react";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton
} from "./ExtendedWarrantyScreenWrapper";
import { buildFlowUrl } from "../../utils/FlowUrlBuilder";
import analyticsService from "../../lib/AnalyticsService";

const CAROUSEL_IMAGES = [
  {
    src: "/extended-warranty/Consider-Warranty.png",
    alt: "Consider the warranty if...",
  },
  {
    src: "/extended-warranty/Skip-Warranty.png",
    alt: "Skip the warranty if...",
  },
];

const ExtendedWarrantyWelcomeScreen = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    analyticsService.trackEvent("Extended Warranty Page View", {
      page: "ew_welcome",
      screen: "welcome",
      flow_type: "extended-warranty",
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Check your vehicle's eligibility",
      description: "See if your car qualifies for extended warranty",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Create your account",
      description: "Quick verification to get started",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Tell us about your needs",
      description: "Answer a few questions about your vehicle",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Get connected with leading providers",
      description: "We'll help you find the right coverage",
    },
  ];

  return (
    <ExtendedWarrantyScreenWrapper>
      <ExtendedWarrantyHeader
        title="Extended Warranty by Ownli"
        subtitle="Find the right extended warranty coverage for your vehicle in just a few minutes."
      >
        <div className="relative mx-auto mt-5 overflow-hidden rounded-lg">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {CAROUSEL_IMAGES.map((image, index) => (
              <img
                key={index}
                src={image.src}
                alt={image.alt}
                className="w-full flex-shrink-0 object-contain"
              />
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-3">
            {CAROUSEL_IMAGES.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  index === currentSlide
                    ? "bg-purple-600"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <ExtendedWarrantyButton onClick={() => {
          analyticsService.trackEvent("Extended Warranty Button Click", {
            button_name: "check-eligibility",
            screen: "welcome",
          });
          onComplete();
        }}>
          Check My Vehicle's Eligibility
          <ArrowRight className="w-4 h-4" />
        </ExtendedWarrantyButton>
      </ExtendedWarrantyHeader>

      <div className="bg-purple-50 p-4 rounded-[20px] text-center">
        <p className="font-bold text-purple-900 text-base mb-1">
          Earn up to $5
        </p>
        <p className="text-sm text-purple-700">
          Complete your vehicle profile and get rewarded through Ownli
        </p>
      </div>

      <ExtendedWarrantyCard variant="info">
        <div className="flex items-center gap-3">
          <Clock className="min-w-[25px] min-h-[25px] text-green" />
          <p className="text-base text-darkGrey">
            Takes less than 3 minutes to check eligibility
          </p>
        </div>
      </ExtendedWarrantyCard>

      <ExtendedWarrantyCard>
        <div>
          <h2 className="text-base font-bold mb-4">
            Here's what to expect:
          </h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="mt-2 min-w-[10px] min-h-[10px] bg-darkGreen rounded-full"></span>
                <div>
                  <h3 className="font-base text-darkGrey">{step.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ExtendedWarrantyCard>

      <div className="space-y-3">
        <ExtendedWarrantyButton onClick={() => {
          analyticsService.trackEvent("Extended Warranty Button Click", {
            button_name: "check-eligibility",
            screen: "welcome",
          });
          onComplete();
        }}>
          Check My Vehicle's Eligibility
          <ArrowRight className="w-4 h-4" />
        </ExtendedWarrantyButton>

        <p className="text-lg text-gray-600 text-center mb-2">
          Check out other ways to save:
        </p>
        <ExtendedWarrantyButton
          onClick={() => {
            analyticsService.trackEvent("Extended Warranty Flow Exit", {
              exit_point: "welcome",
              destination: "insurance_flow",
              flow_type: "extended-warranty",
            });
            window.location.href = buildFlowUrl("insurance");
          }}
          className="!bg-purpleGradient hover:!bg-purpleGradientDark"
        >
          Insurance Offers
        </ExtendedWarrantyButton>
        <div className="text-center">
          <a
            href="https://www.ownli.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center text-sm"
            onClick={() => {
              analyticsService.trackEvent("Extended Warranty Flow Exit", {
                exit_point: "welcome",
                destination: "ownli_app",
                flow_type: "extended-warranty",
              });
            }}
          >
            Other offers at Ownli
          </a>
        </div>

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </ExtendedWarrantyScreenWrapper>
  );
};

export default ExtendedWarrantyWelcomeScreen;
